/**
 * MediatorView — the NEUTRAL best-estimate settlement view.
 *
 * Renders brief.mediator_view (backend war_room_mediator_view.py): the actuarial
 * best estimate E[X] = N − Σ pᵢ·Cᵢ (IFRS 17 / Solvency II unbiased mean). THREE
 * numbers that must never collapse: plaintiff party N, carrier party N−C (their
 * p=1 scenario), neutral mean. On a bare record every pᵢ = 0 so the neutral
 * degenerates to N — correct, not a bug: the carrier's gap is an unproven
 * affirmative defense.
 *
 * Design: K3 ruling (scratch: k3_mediator_design_verdict.md). The sibling
 * (CarrierPositionLedger) shows one party's floor; this shows one mean pinned at
 * the supported net, two parties plotted around it on a TRUE-LINEAR spectrum, and
 * five unproven levers beneath — nothing touches the neutral except evidence. The
 * orange rail means exactly what it means in the sibling: the number this view
 * exists to deliver — here, the neutral. The spectrum domain is computed from the
 * data (never hardcoded), so the view stays correct for every case.
 *
 * Pure presentation — no fetching.
 */
import React from 'react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import type { MediatorView as MediatorViewData, MediatorDefenseFactor } from '@/services/deliberationApi';

export type { MediatorViewData };

const MONO = 'font-mono tabular-nums';
const shortRun = (r?: string | null) => (r ? (r.length > 28 ? `${r.slice(0, 28)}…` : r) : '—');
/** Plain-string money for SVG/inline text (FinancialValue is a block element). */
const money = (v?: number | null, cents = true) =>
  v == null
    ? '—'
    : `$${v.toLocaleString('en-US', {
        minimumFractionDigits: cents ? 2 : 0,
        maximumFractionDigits: cents ? 2 : 0,
      })}`;

/** Grayscale outlined chip — metadata, deliberately not red/orange. */
function MetaChip({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center ${MONO} uppercase`}
      style={{
        fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)', borderRadius: 2, padding: '1px 6px',
      }}
    >
      {children}
    </span>
  );
}

function SectionRule({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-1">
      <span className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}>{text}</span>
      <span className="flex-1" style={{ height: 1, background: 'var(--color-border)' }} />
    </div>
  );
}

// ── Hero: the neutral (orange rail = the number this view delivers) ───────────
function Hero({ mv }: { mv: MediatorViewData }) {
  const n = mv.blocks.neutral;
  const N = mv.blocks.plaintiff.value;
  const capFloor = mv.blocks.carrier.floor_at_policy_cap ?? null;
  const computed = n.value != null;
  const reduction = N != null && n.value != null ? Math.round((N - n.value) * 100) / 100 : null;
  const degenerate = !!n.degenerate;
  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderLeftStyle: 'solid', borderLeftWidth: 3,
        borderLeftColor: computed ? 'var(--color-brand)' : 'var(--color-border)',
        borderRadius: 2, padding: 18,
      }}
    >
      <div className={`${MONO} uppercase`} style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--color-text-muted)' }}>
        Neutral best estimate
      </div>
      {computed ? (
        <>
          <div className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
            <FinancialValue value={n.value} cents className={`${MONO} text-[36px] leading-none font-medium`} />
            <span className="sr-only">neutral best estimate</span>
          </div>
          {/* equality line + formula made arithmetic */}
          <div className={`${MONO} mt-2`} style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            {n.formula || 'N − Σ pᵢ·Cᵢ'} = {money(N)} − {money(reduction)} = {money(n.value)}
          </div>
          <div className="cs-body mt-0.5" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {degenerate
              ? '= N · supported net — anchored here: every pᵢ = 0, no evidence-gated reduction'
              : 'evidence-gated reduction applied; the mean has descended from the supported net'}
          </div>
          {/* CT-P scope label (KAIROS 1386): the neutral is the business-interruption
              supported net (policy_deductions.net_claim). A first-party PROPERTY claim
              (L_PROP / RCV) is a separate track, never composed into this figure — so the
              room can't read the BI neutral as the whole-case number. */}
          <div className="cs-body mt-0.5" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            Business-interruption supported net — any first-party property claim is valued separately, not composed here.
          </div>
          {/* what moves the mean — teaches the orange slide in one line */}
          {degenerate && (
            <div className="cs-body mt-1" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              Nothing attested. The first signed factor lowers E[X] by that factor's Cᵢ.
            </div>
          )}
          {/* scenario strip — two DISTINCT floors, both scenarios not findings */}
          {(n.scenario_if_defense_proven != null || capFloor != null) && (
            <div
              className={`${MONO} mt-3 pt-3`}
              style={{ fontSize: 11, color: 'var(--color-text-muted)', borderTop: '1px solid var(--color-border)' }}
            >
              {n.scenario_if_defense_proven != null && (
                <div>
                  <span className="uppercase" style={{ letterSpacing: '0.06em' }}>Listed levers proven (all p→1) → </span>
                  <span style={{ color: 'var(--color-text-secondary)' }}>{money(n.scenario_if_defense_proven)}</span>
                </div>
              )}
              {capFloor != null && (
                <div style={{ marginTop: 2 }}>
                  <span className="uppercase" style={{ letterSpacing: '0.06em' }}>Policy cap on total credit → </span>
                  <span style={{ color: 'var(--color-text-secondary)' }}>{money(capFloor)}</span>
                </div>
              )}
              <div style={{ color: 'var(--color-text-dim)', marginTop: 2 }}>scenarios, not findings</div>
            </div>
          )}
          <div className={`${MONO} mt-2`} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
            run {shortRun(n.run_id)} · unbiased mean of certified engine outputs · p source: {n.p_source || 'none'}
          </div>
        </>
      ) : (
        <>
          <div className={`${MONO} mt-1`} style={{ fontSize: 30, color: 'var(--color-text-dim)' }}>— NOT RUN</div>
          <div className="cs-body mt-1" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            engines absent — nothing composed, nothing fabricated.
          </div>
        </>
      )}
    </div>
  );
}

// ── Method-variance whisker (dotted → never a confidence interval on the mean) ─
function MethodBand({ band }: { band: NonNullable<MediatorViewData['blocks']['neutral']['band']> }) {
  return (
    <div>
      <SectionRule text="Method variance — beside the mean, never inside it" />
      <div className="flex items-center gap-3 mt-2" style={{ opacity: 0.75 }}>
        <span className={MONO} style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{money(band.low)}</span>
        <span className="flex-1 relative" style={{ height: 8 }}>
          <span
            className="absolute"
            style={{ top: 3, left: 0, right: 0, borderTop: '1px dotted var(--color-text-muted)' }}
          />
          <span className="absolute" style={{ top: 1, left: 0, height: 6, width: 1, background: 'var(--color-text-muted)' }} />
          <span className="absolute" style={{ top: 1, right: 0, height: 6, width: 1, background: 'var(--color-text-muted)' }} />
          {/* mean marker — a plain gray tick, NOT a filled ■ (■ is reserved for party positions). */}
          <span className="absolute" style={{ top: 0, left: '50%', height: 8, width: 1, background: 'var(--color-text-muted)' }} />
        </span>
        <span className={MONO} style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{money(band.high)}</span>
      </div>
      <div className="cs-body mt-1" style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
        {band.note || 'dual-method top-down vs bottom-up · mean-preserving · not a reduction, not a range on E[X].'}
      </div>
    </div>
  );
}

// ── Spectrum: true-linear number line, domain computed from the data ──────────
// Three ticks only — Policy cap · Carrier · Best estimate. The plaintiff is NOT
// on the axis: on a bare record it shares the mean's dollar, and putting it on the
// tick makes it read as "the mediator adopted the plaintiff." Plaintiff lives in
// the party-row list below; the ONE orange mark is the mean.
function Spectrum({ mv }: { mv: MediatorViewData }) {
  const carrier = mv.blocks.carrier.value;
  const neutral = mv.blocks.neutral.value;
  const floorCap = mv.blocks.carrier.floor_at_policy_cap ?? null;
  if (carrier == null || neutral == null) return null;

  const pts = [carrier, neutral, floorCap].filter((v): v is number => v != null);
  const lo = Math.min(...pts);
  const hi = Math.max(...pts);
  const span = hi - lo || 1;
  const pad = span * 0.12;
  const dLo = lo - pad;
  const dHi = hi + pad;

  const W = 672, H = 116, mX = 48, axisY = 70;
  const x = (v: number) => mX + ((v - dLo) / (dHi - dLo)) * (W - 2 * mX);
  const MF = 'DM Mono, monospace';
  const anc = (px: number): 'start' | 'middle' | 'end' => (px > W - 150 ? 'end' : px < 150 ? 'start' : 'middle');

  // The remaining UNPROVEN gap between the mean and the carrier scenario (shrinks
  // as evidence lands: on a bare record it's the full N−carrier, less once p>0).
  const arrowGap = Math.round((neutral - carrier) * 100) / 100;
  const xCarrier = x(carrier), xNeutral = x(neutral);
  const arrowMid = (xNeutral + xCarrier) / 2;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="settlement position spectrum" style={{ display: 'block' }}>
      <defs>
        <marker id="mvArrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
          <path d="M0,0 L6,3.5 L0,7 Z" fill="var(--color-text-muted)" />
        </marker>
      </defs>

      {/* top band — the dashed left-only arrow: the mean slides left toward the carrier */}
      <text x={arrowMid} y={16} textAnchor="middle" fontSize={9} fill="var(--color-text-muted)" style={{ fontFamily: MF }}>
        E[X] slides left only as evidence lands
      </text>
      <line x1={xNeutral - 4} y1={30} x2={xCarrier + 6} y2={30}
        stroke="var(--color-text-muted)" strokeWidth={1} strokeDasharray="3 3" markerEnd="url(#mvArrow)" />
      {arrowGap > 0 && (
        <text x={arrowMid} y={44} textAnchor="middle" fontSize={9} fill="var(--color-text-dim)" style={{ fontFamily: MF }}>
          Δ {money(arrowGap)} — unproven
        </text>
      )}

      {/* axis */}
      <line x1={mX} y1={axisY} x2={W - mX} y2={axisY} stroke="var(--color-border)" strokeWidth={1} />

      {/* policy-cap floor — ghost tick */}
      {floorCap != null && (
        <g opacity={0.5}>
          <line x1={x(floorCap)} y1={axisY - 6} x2={x(floorCap)} y2={axisY + 6} stroke="var(--color-text-muted)" strokeWidth={1} />
          <text x={x(floorCap)} y={axisY + 22} textAnchor={anc(x(floorCap))} fontSize={9} fill="var(--color-text-muted)" style={{ fontFamily: MF }}>POLICY CAP</text>
          <text x={x(floorCap)} y={axisY + 33} textAnchor={anc(x(floorCap))} fontSize={9} fill="var(--color-text-dim)" style={{ fontFamily: MF }}>{money(floorCap)}</text>
        </g>
      )}

      {/* carrier — hollow square (advocacy) */}
      <rect x={xCarrier - 4} y={axisY - 4} width={8} height={8} fill="none" stroke="var(--color-text-secondary)" strokeWidth={1.2} />
      <text x={xCarrier} y={axisY + 22} textAnchor={anc(xCarrier)} fontSize={9} fill="var(--color-text-secondary)" style={{ fontFamily: MF }}>CARRIER</text>
      <text x={xCarrier} y={axisY + 33} textAnchor={anc(xCarrier)} fontSize={9} fill="var(--color-text-muted)" style={{ fontFamily: MF }}>{money(carrier)}</text>

      {/* best estimate — the ONE orange mean (the answer this view delivers) */}
      <rect x={xNeutral - 4} y={axisY - 4} width={8} height={8} fill="var(--color-brand)" />
      <text x={xNeutral} y={axisY + 22} textAnchor={anc(xNeutral)} fontSize={9} fill="var(--color-text-primary)" style={{ fontFamily: MF }}>BEST ESTIMATE</text>
      <text x={xNeutral} y={axisY + 33} textAnchor={anc(xNeutral)} fontSize={9} fill="var(--color-text-secondary)" style={{ fontFamily: MF }}>{money(neutral)}</text>
    </svg>
  );
}

// ── Party ledger rows (plaintiff ■ / carrier □) ──────────────────────────────
function PartyRow({
  filled, title, value, subline, note, chip,
}: {
  filled: boolean; title: string; value: number | null; subline?: React.ReactNode; note?: string; chip?: string;
}) {
  return (
    <div className="py-3" style={{ borderTop: '1px solid var(--color-border-faint, var(--color-border))' }}>
      <div className="flex items-baseline gap-3">
        <span
          className="mt-1.5 h-[6px] w-[6px] shrink-0"
          style={filled ? { background: 'var(--color-text-secondary)' } : { border: '1px solid var(--color-text-muted)' }}
        />
        <div className="flex-1 min-w-0">
          <div className="cs-body" style={{ color: filled ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', fontSize: 13 }}>{title}</div>
          {subline && <div className={MONO} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>{subline}</div>}
        </div>
        {chip && <div className="shrink-0"><MetaChip>{chip}</MetaChip></div>}
        <div className="text-right shrink-0" style={{ width: 132 }}>
          <FinancialValue value={value} cents className={MONO} />
        </div>
      </div>
      {note && (
        <p className="mt-1.5 cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 12, margin: '6px 0 0 20px' }}>
          <span className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', marginRight: 6 }}>Note —</span>
          {note}
        </p>
      )}
    </div>
  );
}

// ── Bridge table: five levers, none pulled ───────────────────────────────────
function BridgeTable({ mv }: { mv: MediatorViewData }) {
  const rows = mv.defense_itemization || [];
  if (!rows.length) return null;
  const N = mv.blocks.plaintiff.value;
  const carrier = mv.blocks.carrier.value;
  const neutral = mv.blocks.neutral.value;
  const totalC = rows.reduce((s, r) => s + (r.engine_delta || 0), 0);
  const capMax = mv.blocks.carrier.credit_max ?? null;
  const applied = N != null && neutral != null ? Math.round((N - neutral) * 100) / 100 : 0;

  const cell: React.CSSProperties = { fontSize: 9, letterSpacing: '0.06em', color: 'var(--color-text-dim)' };
  return (
    <div>
      {/* header */}
      <div className="flex items-baseline gap-3 pb-1" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <span className="flex-1" />
        <span className={`${MONO} uppercase text-right`} style={{ ...cell, width: 44 }}>p</span>
        <span className={`${MONO} uppercase text-right`} style={{ ...cell, width: 128 }}>Would lower E[X] by</span>
        <span className={`${MONO} uppercase text-right`} style={{ ...cell, width: 132 }}>E[X] if proven alone</span>
      </div>
      {rows.map((r: MediatorDefenseFactor) => {
        const zero = !r.engine_delta;
        const ifAlone = N != null && r.engine_delta != null ? Math.round((N - r.engine_delta) * 100) / 100 : null;
        return (
          <div key={r.key} className="py-2.5" style={{ borderTop: '1px solid var(--color-border-faint, var(--color-border))' }}>
            <div className="flex items-baseline gap-3">
              <span className="mt-1.5 h-[5px] w-[5px] shrink-0" style={{ border: '1px solid var(--color-text-muted)' }} />
              <div className="flex-1 min-w-0">
                <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontSize: 13 }}>{r.label}</div>
                {r.carrier_must_prove && (
                  <div className="cs-body" style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
                    <span className={`${MONO} uppercase`} style={{ fontSize: 9, marginRight: 5 }}>Carrier must prove —</span>{r.carrier_must_prove}
                  </div>
                )}
              </div>
              <span className={`${MONO} text-right`} style={{ width: 44, fontSize: 12, color: 'var(--color-text-muted)' }}>{(r.p ?? 0).toFixed(2)}</span>
              <span className={`${MONO} text-right`} style={{ width: 128, fontSize: 12, color: 'var(--color-text-secondary)', opacity: 0.6 }}>
                {zero ? '$0.00' : `− ${money(r.engine_delta)}`}
              </span>
              <span className={`${MONO} text-right`} style={{ width: 132, fontSize: 12, color: 'var(--color-text-dim)', opacity: zero ? 1 : 0.6 }}>
                {zero ? 'no delta this run' : `→ ${money(ifAlone)}`}
              </span>
            </div>
          </div>
        );
      })}
      {/* bridge footer — the doctrine in arithmetic */}
      <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--color-text-secondary)' }}>
        <div className={MONO} style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
          Σ if all {rows.length} proven → E[X] = {money(carrier)} (Δ −{money(totalC)}) = the carrier position
        </div>
        <div className={MONO} style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
          today (Σ p·C = {money(applied)}) → E[X] = {money(neutral)}
        </div>
        {capMax != null && (
          <div className="cs-body" style={{ fontSize: 10.5, color: 'var(--color-text-dim)', marginTop: 4 }}>
            Listed levers {money(totalC)}; policy cap {money(capMax)}. The mean uses attested
            {' '}pᵢ·Cᵢ, then min(Σ, cap) — the total credit can never exceed the 30% cap.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Excluded figures + neutrality stamp ──────────────────────────────────────
function ExcludedRow({ label, subline }: { label: string; subline: string }) {
  return (
    <div className="py-2 flex items-baseline gap-3" style={{ borderTop: '1px solid var(--color-border-faint, var(--color-border))' }}>
      <span className="mt-1.5 h-[5px] w-[5px] shrink-0" style={{ border: '1px solid var(--color-text-muted)' }} />
      <div className="flex-1 min-w-0">
        <div className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{label}</div>
        <div className="cs-body" style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>{subline}</div>
      </div>
      <div className="shrink-0"><MetaChip>Not used</MetaChip></div>
      <div className="text-right shrink-0" style={{ width: 60 }}>
        <span className={MONO} style={{ fontSize: 13, color: 'var(--color-text-dim)' }}>—</span>
      </div>
    </div>
  );
}

function NeutralityStamp({ mv }: { mv: MediatorViewData }) {
  const nc = mv.neutrality_check || {};
  // Keep the NO/NO/NO/NONE column scannable in one saccade: the p-source
  // parenthetical drops to a micro subline instead of sprawling across the column.
  const pSrc = nc.p_source || 'none';
  const pMatch = pSrc.match(/^([^(]+?)\s*\(([^)]*)\)\s*$/);
  const pVerdict = (pMatch ? pMatch[1] : pSrc).trim().toUpperCase();
  const pSub = pMatch ? pMatch[2].trim() : null;
  const rows: [string, string, string | null][] = [
    ['low anchor = default defense', nc.low_anchor_is_default_defense ? 'YES' : 'NO', null],
    ['uses fair-settlement P50', nc.uses_fair_settlement ? 'YES' : 'NO', null],
    ['uses defendant LLM dollars', nc.uses_llm_dollars ? 'YES' : 'NO', null],
    ['p source', pVerdict, pSub],
  ];
  return (
    <div className="mt-2" style={{ border: '1px solid var(--color-border)', borderRadius: 2, padding: 14 }}>
      <div className={`${MONO} uppercase mb-2`} style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}>
        Neutrality check — how this number stays neutral
      </div>
      {rows.map(([label, verdict, sub]) => (
        <div key={label} className="mb-0.5">
          <div className="flex items-baseline gap-2" style={{ fontSize: 11 }}>
            <span className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
            <span className="flex-1" style={{ borderBottom: '1px dotted var(--color-border)', transform: 'translateY(-3px)' }} />
            <span className={`${MONO} uppercase`} style={{ color: 'var(--color-text-secondary)' }}>{verdict}</span>
          </div>
          {sub && <div className="cs-body" style={{ fontSize: 10, color: 'var(--color-text-dim)', marginTop: 1 }}>{sub}</div>}
        </div>
      ))}
      {nc.symmetry && (
        <p className="cs-body mt-2" style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '8px 0 0' }}>
          <span className={`${MONO} uppercase`} style={{ fontSize: 9, color: 'var(--color-text-dim)', marginRight: 6 }}>Note —</span>
          {nc.symmetry}
        </p>
      )}
    </div>
  );
}

// ── Copy for the room — the mediation footnote as a one-click string ─────────
function buildFootnote(data: MediatorViewData): string {
  const n = data.blocks.neutral, c = data.blocks.carrier;
  const nc = data.neutrality_check || {};
  const allNo = !nc.low_anchor_is_default_defense && !nc.uses_fair_settlement && !nc.uses_llm_dollars;
  const pTag = n.p_source && n.p_source !== 'none' ? 'attested p' : 'pᵢ=0';
  // Gap-2: the footnote travels to the room without UI context, so it carries BOTH
  // damage tracks — the BI neutral AND the first-party property (RCV + payable) —
  // clearly labeled as separate, never summed. Property lines appear only on a
  // property claim; a BI-only footnote keeps the Gap-1 single-scope line.
  const p = data.property_track;
  const hasProp = !!(p && p.rcv != null);
  const attestor = data.provenance?.attestor?.length ? data.provenance.attestor.join(', ') : '—';
  return [
    hasProp ? 'Mediation footnote — two damage tracks (never summed)' : 'Mediation footnote — neutral best estimate',
    `BI neutral        ${money(n.value)}  (${pTag})`,
    hasProp ? `Property RCV      ${money(p!.rcv)}` : null,
    hasProp && p!.payable != null ? `Property payable  ${money(p!.payable)}  (L_PROP${p!.payable_basis ? ` · ${p!.payable_basis}` : ''})` : null,
    hasProp
      ? (p!.reconciled
          ? (p!.unmatched_line_count != null
              ? `Unmatched lines   ${p!.unmatched_line_count}  (contractor/adjuster)`
              : 'Property basis    reconciled — unmatched count unavailable')
          : 'Property basis    single-estimate — no contractor/adjuster reconciliation')
      : null,
    `Carrier           ${money(c.value)}  (C=${money(c.credit)}, unproven)`,
    c.floor_at_policy_cap != null ? `Policy-cap floor  ${money(c.floor_at_policy_cap)}` : null,
    `Neutrality checks: ${allNo ? 'all NO' : 'see board'}`,
    `Run ${n.run_id || '—'} · attestor ${attestor} · attorney work product`,
    hasProp
      ? 'BI neutral and property are separate tracks — never summed; a whole-case total is an unattested composition.'
      : 'Scope: business-interruption supported net — any property claim valued separately.',
    'Not a party demand.',
  ].filter(Boolean).join('\n');
}

function CopyForRoom({ data }: { data: MediatorViewData }) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildFootnote(data));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked (insecure ctx / permissions) — no-op */ }
  };
  return (
    <button
      type="button" onClick={onCopy}
      className={`${MONO} uppercase`}
      style={{
        fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)', borderRadius: 2, padding: '3px 8px',
        background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      {copied ? 'Copied ✓' : 'Copy for the room'}
    </button>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────
export function MediatorView({ data }: { data?: MediatorViewData | null }) {
  if (!data) return null;
  const n = data.blocks.neutral;
  const plaintiff = data.blocks.plaintiff;
  const carrier = data.blocks.carrier;
  const band = n.band || null;
  const excl = data.excluded_figures || {};
  const ceiling = plaintiff.excluded?.attorney_supportable_ceiling ?? null;

  const pState = n.p_source && n.p_source !== 'none';
  const defAlt = excl.alternative_damages;
  const defMed = excl.defendant_mediation_position;

  return (
    <div
      style={{ maxWidth: 720, background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 2, padding: 24 }}
      data-testid="mediator-view"
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="cs-display" style={{ fontSize: 20, lineHeight: 1.1 }}>Mediator View — Neutral Best Estimate</h2>
        <div className="flex items-center gap-2 shrink-0">
          <MetaChip title="every failure-to-mitigate factor is unproven on this record">
            {pState ? 'Evidence entered' : 'Bare record — all pᵢ = 0'}
          </MetaChip>
          <CopyForRoom data={data} />
        </div>
      </div>

      <Hero mv={data} />

      {/* Gap-2: two-track damage view. Gap-1 killed "is $155k the whole case?"; this
          shows what else there is — the first-party PROPERTY track (RCV + payable),
          BESIDE the BI neutral, NEVER summed into it. Renders only on a property claim
          (property_track present); BI-only stays byte-identical. */}
      {data.property_track && data.property_track.rcv != null && (() => {
        const p = data.property_track!;
        const attestor = data.provenance?.attestor?.length ? data.provenance.attestor.join(', ') : '—';
        // D4 (Fable): the p-tag must reflect the record — never hardcode "attested p"
        // (d6d88aea is a bare record, pᵢ=0). Matches the header chip + the footnote.
        const pTag = n.p_source && n.p_source !== 'none' ? 'attested p' : 'pᵢ=0';
        const row = (label: React.ReactNode, value: number | null | undefined) => (
          <div className="flex items-baseline justify-between gap-3 mt-1">
            <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
            <span style={{ color: 'var(--color-text-secondary)' }}>{money(value)}</span>
          </div>
        );
        return (
          <>
            <SectionRule text="Damage tracks — BI neutral + first-party property" />
            <div className={MONO} style={{ fontSize: 12 }}>
              {row(`Business interruption — neutral (${pTag})`, n.value)}
              {/* D3 (Fable): payable_basis describes the PAYABLE (l_prop_figure chain
                  net_property_payment → payable_rcv → rcv_total), NOT the RCV — put it on
                  the payable row. On real d6d88aea the basis is "net_property_payment". */}
              {row('First-party property — RCV', p.rcv)}
              {p.payable != null && row(<>First-party property — payable (L_PROP){p.payable_basis ? <span style={{ color: 'var(--color-text-dim)' }}> · {p.payable_basis}</span> : null}</>, p.payable)}
              <div className="mt-2" style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
                {p.reconciled
                  ? (p.unmatched_line_count != null
                      ? `${p.unmatched_line_count} contractor/adjuster line${p.unmatched_line_count === 1 ? '' : 's'} unmatched`
                      : 'reconciled — unmatched count unavailable')
                  : 'single-estimate basis — no contractor/adjuster reconciliation'}
                {' · two tracks, never summed (a whole-case total would be an unattested composition)'}
              </div>
              <div className="mt-1" style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
                run {shortRun(p.run_id)} · attestor {attestor} · attorney work product
              </div>
            </div>
          </>
        );
      })()}

      {band && <MethodBand band={band} />}

      {plaintiff.value != null && carrier.value != null && n.value != null && (
        <>
          <SectionRule text="Positions — two party claims, one mean" />
          <div className="mt-2"><Spectrum mv={data} /></div>

          <PartyRow
            filled
            title="Plaintiff supported — party position"
            value={plaintiff.value}
            subline={plaintiff.basis ? `${plaintiff.basis} · run ${shortRun(plaintiff.run_id)} · attestable` : undefined}
            note={
              'The neutral is anchored here on this record. Attorney-supportable ceiling ' +
              `(${money(ceiling)}) — aspirational, above the supported net — excluded from every block.`
            }
          />
          <PartyRow
            filled={false}
            title="Carrier position — their scenario"
            value={carrier.value}
            subline={<>mitigation · engine defaults, unattested · <span style={{ whiteSpace: 'nowrap' }}>credit {money(carrier.credit)}</span></>}
            chip="Advocacy — not a neutral bound"
            note={carrier.caveat}
          />
        </>
      )}

      {data.defense_itemization?.length > 0 && (
        <>
          <SectionRule text="Defense itemization — what moves the neutral" />
          <p className="cs-body mb-1" style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
            evidence-gated probability · 0 = unproven · all levers at engine defaults · burden on the carrier
          </p>
          <BridgeTable mv={data} />
        </>
      )}

      <SectionRule text="Excluded from every block — cross-checks only" />
      <ExcludedRow label="Fair-settlement P50" subline="sub-N spread structurally invented — not composed" />
      <ExcludedRow label="Adjuster value" subline="per-rescore LLM-echo ratchet — not composed" />
      {(defAlt != null || defMed != null) && (
        // Collapsed so the LLM dollars can NEVER be read as a fourth tick near the axis.
        <details className="mt-2">
          <summary className={`${MONO} uppercase`} style={{ fontSize: 9, letterSpacing: '0.06em', color: 'var(--color-text-dim)', cursor: 'pointer' }}>
            Defendant LLM cross-checks (never composed)
          </summary>
          <div className={`${MONO} mt-1`} style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
            {defAlt != null && <span style={{ whiteSpace: 'nowrap' }}>alternative damages {money(defAlt as number, false)}</span>}
            {defMed != null && <> · <span style={{ whiteSpace: 'nowrap' }}>mediation position {money(defMed as number, false)}</span></>}
          </div>
        </details>
      )}

      <div className="mt-4"><NeutralityStamp mv={data} /></div>

      {(data.caveats || []).length > 0 && (
        <ul className="mt-3 space-y-1" style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
          {data.caveats.map((c, i) => (
            <li key={i} className="cs-body flex gap-2" style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>
              <span style={{ color: 'var(--color-text-dim)' }}>—</span>{c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default MediatorView;
